import React, { useEffect, useRef, useState } from 'react';
import { App, Button, Form, Input, Modal, Select } from 'antd';
import { useTranslate } from '@/translations';
import {
  isReleaseNotePublished,
  ReleaseNote,
  ReleaseNoteApp,
  SaveReleaseNote,
} from '@/types';
import { isQuillEmpty, QuillEditor } from '@/ui/components';

type FormValues = {
  app: ReleaseNoteApp;
  versionLabel: string;
  title: string;
};

type ReleaseNoteEditorPopupProps = {
  open: boolean;
  saving?: boolean;
  note?: ReleaseNote | null;
  defaultApp?: ReleaseNoteApp;
  onClose: () => void;
  onSubmit: (values: SaveReleaseNote) => void;
};

export const ReleaseNoteEditorPopup: React.FC<ReleaseNoteEditorPopupProps> = ({
  open,
  saving,
  note,
  defaultApp,
  onClose,
  onSubmit,
}) => {
  const { message } = App.useApp();
  const { t } = useTranslate();
  const [form] = Form.useForm<FormValues>();
  const [notes, setNotes] = useState('');
  const saveAction = useRef<'save' | 'publish' | 'unpublish'>('save');
  const isPublished = isReleaseNotePublished(note);

  useEffect(() => {
    if (!open) return;

    saveAction.current = 'save';
    setNotes(note?.notes ?? '');
    form.resetFields();
    form.setFieldsValue({
      app: note?.app ?? defaultApp ?? 'OneView',
      versionLabel: note?.versionLabel,
      title: note?.title,
    });
  }, [defaultApp, form, note, open]);

  const submit = (action: 'save' | 'publish' | 'unpublish') => {
    saveAction.current = action;
    form.submit();
  };

  return (
    <Modal
      open={open}
      title={note ? t('releaseNotesEdit') : t('releaseNotesCreate')}
      onCancel={onClose}
      destroyOnHidden
      width={720}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('cancel')}
        </Button>,
        <Button
          key="save"
          type={isPublished ? 'primary' : 'default'}
          loading={saving}
          onClick={() => submit('save')}
        >
          {t('releaseNotesSave')}
        </Button>,
        isPublished ? (
          <Button
            key="unpublish"
            type="primary"
            loading={saving}
            onClick={() => submit('unpublish')}
          >
            {t('releaseNotesUnpublish')}
          </Button>
        ) : (
          <Button
            key="publish"
            type="primary"
            loading={saving}
            onClick={() => submit('publish')}
          >
            {t('releaseNotesPublish')}
          </Button>
        ),
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => {
          if (isQuillEmpty(notes)) {
            message.error(t('releaseNotesBodyRequired'));
            return;
          }
          const payload: SaveReleaseNote = {
            app: values.app,
            versionLabel: values.versionLabel.trim(),
            title: values.title.trim(),
            notes,
          };
          if (saveAction.current === 'publish') payload.publish = true;
          if (saveAction.current === 'unpublish') payload.publish = false;
          onSubmit(payload);
        }}
      >
        <Form.Item
          name="app"
          label={t('releaseNotesApp')}
          rules={[{ required: true }]}
        >
          <Select
            options={[
              { value: 'OneView', label: 'OneView' },
              { value: 'TMS', label: 'TMS' },
            ]}
          />
        </Form.Item>
        <Form.Item
          name="versionLabel"
          label={t('releaseNotesVersion')}
          rules={[{ required: true }, { max: 50 }]}
        >
          <Input maxLength={50} />
        </Form.Item>
        <Form.Item
          name="title"
          label={t('releaseNotesTitle')}
          rules={[{ required: true }, { max: 200 }]}
        >
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item label={t('releaseNotesBody')} required>
          <QuillEditor value={notes} onChange={setNotes} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
